import React from 'react';
import DocAiSummaryBoundary from './DocAiSummaryBoundary';
import {
  formSourceState,
  getFormSourceKey,
  getSourceKey,
  hasNewerFormVersion,
  hasNewerSignedCopy,
  isPrimaryParty,
} from './docAiSummaryRules';

// One "Generate summary/checklist" / "View summary/checklist" entry for a document's ⋮ menu
// (or the PDF preview header — pass `className`). Renders nothing unless the
// server confirmed the feature is on and the viewer is a primary party, and the
// row is an eligible PDF — so dropping it into a menu can never change what
// other users see.
//
// area: 'documents' (Transaction Documents sub-tab, default) or 'forms'.
const MenuItemInner = ({ docAi, doc, role, onSelect, area = 'documents', className = 'txn-doc-kebab-item' }) => {
  if (!docAi) return null;
  const isForms = area === 'forms';
  const baseKey = isForms ? getFormSourceKey(doc, role) : getSourceKey(doc, role);
  if (!baseKey) return null;

  // FR-4: the status list failed for a reason other than "feature off" / "no access".
  // Show an error state (with Retry) to viewers who look like primary parties.
  if (docAi.loadError) {
    if (!isPrimaryParty(role) && !docAi.viewerIsPrimary) return null;
    return (
      <button
        type="button"
        className={className}
        title="Couldn't load summaries. Click to retry."
        onClick={(e) => {
          e.stopPropagation();
          docAi.refresh();
        }}
      >
        Ria Summary unavailable · Retry
      </button>
    );
  }

  // `enabled` is only true once the server accepted this viewer as a primary party (FR-14).
  if (!docAi.enabled) return null;
  const sourceKey = isForms ? { ...baseKey, sourceState: formSourceState(doc, baseKey.sourceKind) } : baseKey;

  const status = docAi.statusFor(sourceKey.key);
  const isPending = status?.status === 'PENDING';
  const isReady = !isPending && (status?.status === 'READY' || status?.currentVersion > 0);
  const newer = isForms ? hasNewerFormVersion(status, doc) : hasNewerSignedCopy(status, doc);

  let label = 'Generate summary/checklist';
  if (isPending) label = 'Ria Summary (generating…)';
  else if (isReady) label = 'View summary/checklist';

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        if (onSelect) onSelect();
        docAi.open(doc, sourceKey, isReady || isPending ? 'view' : 'generate', { clientStale: isForms && newer });
      }}
    >
      {label}
      {newer && (
        <span
          title="Document updated — regenerate to update the summary"
          style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#6c5ce7', marginLeft: 6, verticalAlign: 'middle' }}
        />
      )}
    </button>
  );
};

const AiSummaryMenuItem = (props) => (
  <DocAiSummaryBoundary>
    <MenuItemInner {...props} />
  </DocAiSummaryBoundary>
);

export default AiSummaryMenuItem;
